// Opaque stable rule identifiers and their markdown markers (issue #89).
//
// A rule is a markdown bullet under docs/instructions/rules/**. Each gets an
// opaque id RL-<hash> embedded as a trailing HTML comment:
//
//   - Keep changes deterministic and test-backed. <!-- @rule RL-7f3a -->
//
// The marker is invisible in rendered markdown, survives reformatting, and is
// never edited by hand. The map keys off the id, not text or line numbers, so
// a rule keeps its id through reword / file move / split. Stability comes from
// persisting the marker in the file — not from the hash being edit-invariant.

import { createHash } from 'node:crypto';

const ID_PREFIX = 'RL-';
const MIN_HEX = 4;
const MAX_HEX = 12;

// Matches a trailing <!-- @rule RL-xxxx --> marker and captures the id.
const MARKER_RE = /<!--\s*@rule\s+(RL-[0-9a-f]+)\s*-->/i;

export function sha256(input: string): string {
  return createHash('sha256').update(input).digest('hex');
}

// sha256 of the rule's verbatim text — the drift signal for RS-RULE-EDITED.
export function ruleTextHash(text: string): string {
  return sha256(text);
}

// Returns the rule id embedded in a markdown line, or null if unmarked.
export function parseRuleMarker(line: string): string | null {
  const m = MARKER_RE.exec(line);
  if (!m) {
    return null;
  }
  // Canonical form: uppercase RL- prefix, lowercase hex.
  return `${ID_PREFIX}${m[1].slice(ID_PREFIX.length).toLowerCase()}`;
}

// Removes the marker from a line and returns the clean text + the id (if any).
export function stripRuleMarker(line: string): { text: string; id: string | null } {
  const id = parseRuleMarker(line);
  const text = line.replace(MARKER_RE, '').trimEnd();
  return { text, id };
}

// Appends a marker to a bullet line. Idempotent: a line that already carries
// the same id is returned unchanged; a stale marker is replaced.
export function embedRuleMarker(line: string, id: string): string {
  const existing = parseRuleMarker(line);
  if (existing === id) {
    return line;
  }
  const clean = line.replace(MARKER_RE, '').trimEnd();
  return `${clean} <!-- @rule ${id} -->`;
}

// Mints a fresh opaque id for a rule, seeded by its source path + text so the
// same input is deterministic within a run, extending the hex length on
// collision against ids already assigned in this pass.
export function mintRuleId(source: string, text: string, takenIds: ReadonlySet<string>): string {
  const digest = sha256(`${source} ${text}`);
  for (let len = MIN_HEX; len <= MAX_HEX; len++) {
    const candidate = `${ID_PREFIX}${digest.slice(0, len)}`;
    if (!takenIds.has(candidate)) {
      return candidate;
    }
  }
  // Astronomically unlikely; fall back to the full digest.
  return `${ID_PREFIX}${digest}`;
}

export function isRuleId(value: string): boolean {
  return /^RL-[0-9a-f]{4,}$/i.test(value);
}
